/**
 * Predictive search — full-width desktop panel helper.
 *
 * Three jobs:
 *  1. Anchor the panel right below the header (the header height varies with
 *     the announcement bar, sticky state and transparent header).
 *  2. Let the shopper type straight into the theme's inline header search bar
 *     while the panel is open: keystrokes are forwarded to the drawer's own
 *     (hidden) input, which is what predictive-search-drawer.js listens to.
 *  3. Keep the focus trap from yanking focus out of that header field, by
 *     registering the header search bar as a second trap container.
 *
 * Hand-authored asset. Not generated by the webpack pipeline.
 */
(function () {
  var DESKTOP_QUERY = 'screen and (min-width: 1000px)';
  var OPEN_CLASS = 'search-panel-open';
  var EXTERNAL_INPUT_CLASS = 'predictive-search--external-input';

  function init() {
    var drawer = document.getElementById('search-drawer');

    if (!drawer || !drawer.classList.contains('predictive-search--panel')) {
      return;
    }

    var drawerInput = drawer.querySelector('[name="q"]');
    var root = document.documentElement;
    var mediaQuery = window.matchMedia(DESKTOP_QUERY);

    /**
     * The theme renders more than one inline search bar (one per header
     * layout); only one of them is ever on screen. Below the desktop
     * breakpoint they are all hidden and the panel uses its own input.
     */
    function externalSearchBar() {
      if (!mediaQuery.matches) {
        return null;
      }

      var bars = document.querySelectorAll('.header__search-bar');

      for (var i = 0; i < bars.length; i++) {
        if (bars[i].getClientRects().length > 0 && bars[i].querySelector('[name="q"]')) {
          return bars[i];
        }
      }

      return null;
    }

    function syncMode() {
      var bar = externalSearchBar();
      drawer.classList.toggle(EXTERNAL_INPUT_CLASS, bar !== null);
      return bar;
    }

    function positionPanel() {
      if (!mediaQuery.matches) {
        drawer.style.removeProperty('--search-panel-top');
        return;
      }

      var header = document.querySelector('store-header.header');

      if (!header) {
        return;
      }

      var bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
      drawer.style.setProperty('--search-panel-top', bottom + 'px');
    }

    /* Replay the value into the drawer input so the theme's own debounced
       search handler runs exactly as if the shopper typed inside the drawer. */
    function forwardValue(value) {
      if (!drawerInput || drawerInput.value === value) {
        return;
      }

      drawerInput.value = value;
      drawerInput.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
    }

    function setTrapContainers(containers) {
      try {
        drawer.focusTrap.updateContainerElements(containers);
      } catch (error) {
        /* Focus trap not available — typing still works, focus is just not trapped. */
      }
    }

    document.addEventListener('input', function (event) {
      var input = event.target;

      if (!input || input.name !== 'q' || drawer.contains(input)) {
        return;
      }

      var bar = externalSearchBar();

      if (!bar || !bar.contains(input)) {
        return;
      }

      if (!drawer.hasAttribute('open')) {
        drawer.open = true;
      }

      forwardValue(input.value);
    });

    drawer.addEventListener('openable-element:open', function () {
      positionPanel();
      root.classList.add(OPEN_CLASS);

      var bar = syncMode();

      if (!bar) {
        return;
      }

      /* Adding the header bar as a trap container lets focus (and therefore
         typing) live outside the drawer without being pulled back in. */
      setTrapContainers([drawer, bar]);

      var input = bar.querySelector('[name="q"]');

      if (!input) {
        return;
      }

      forwardValue(input.value);

      /* The theme activates the trap on a 150ms timeout — focus after it. */
      window.setTimeout(function () {
        if (drawer.hasAttribute('open')) {
          input.focus({ preventScroll: true });
        }
      }, 160);
    });

    drawer.addEventListener('openable-element:close', function () {
      root.classList.remove(OPEN_CLASS);
      setTrapContainers([drawer]);
    });

    window.addEventListener('resize', function () {
      syncMode();

      if (drawer.hasAttribute('open')) {
        positionPanel();
      }
    }, { passive: true });

    syncMode();
    positionPanel();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();